import {
  Person,
  RequestFile,
  RequestFileCategory,
  RequestMistakeRequestAssoc,
  RequestMistakeType,
  User,
} from '@core/types';

import { WorkflowNodeForm } from '../form/form';
import {
  ArchiveReasonDetailsMap,
  RequestWorkflow,
  RequestWorkflowArchiveReason,
  ValueOf,
  WorkflowFormField,
  WorkflowFormSubmission,
  WorkflowFormSubmissionFile,
} from '@workflows/types';

export const REQUEST_WORKFLOW_EVENT_TYPE = {
  REQUEST_WORKFLOW_CREATED: 'requestWorkflowCreated',
  REQUEST_WORKFLOW_SUBMITTED: 'requestWorkflowSubmitted',
  REQUEST_WORKFLOW_ARCHIVED: 'requestWorkflowArchived',
  REQUEST_WORKFLOW_UNARCHIVED: 'requestWorkflowUnarchived',
  REQUEST_WORKFLOW_REQUEST_PRODUCT_ID_UPDATED:
    'requestWorkflowRequestProductIdUpdated',
  REQUEST_MISTAKES_UPDATED: 'requestMistakesUpdated',
  FILE_UPLOADED_TO_SUBMISSION: 'fileUploadedToSubmission',
  FILE_DELETED_FROM_SUBMISSION: 'fileDeletedFromSubmission',
  REQUEST_WORKFLOW_SUBMISSION_SYNCED: 'requestWorkflowSubmissionSynced',
  REQUEST_WORKFLOW_IN_PROGRESS: 'requestWorkflowInProgress',
  REQUEST_RELEASED: 'requestReleased',
} as const;
export type RequestWorkflowEventType = ValueOf<
  typeof REQUEST_WORKFLOW_EVENT_TYPE
>;

export const REQUEST_WORKFLOW_ACTOR_TYPE = {
  USER: 'user',
  SYSTEM: 'system',
} as const;
export type RequestWorkflowActorType = ValueOf<
  typeof REQUEST_WORKFLOW_ACTOR_TYPE
>;

export type RequestWorkflowEventActor =
  | { actorType: typeof REQUEST_WORKFLOW_ACTOR_TYPE.SYSTEM; actorId?: never }
  | { actorType: typeof REQUEST_WORKFLOW_ACTOR_TYPE.USER; actorId: User['id'] };

export type BaseRequestWorkflowEvent = {
  id: number;
  requestWorkflowId: RequestWorkflow['id'];
  createdAt: Date;
};

// Мета для архивации с дискриминацией по reason
type RequestWorkflowArchivedMeta = {
  [R in RequestWorkflowArchiveReason]: {
    reason: R;
  } & (R extends keyof ArchiveReasonDetailsMap
    ? { reasonDetails: ArchiveReasonDetailsMap[R] }
    : { reasonDetails?: undefined });
}[RequestWorkflowArchiveReason];

// Какие поля в meta имеет каждый EventType.
// Если тип не имеет meta, он не прописывается тут.
export type RequestWorkflowEventMetaByType = {
  [REQUEST_WORKFLOW_EVENT_TYPE.REQUEST_WORKFLOW_ARCHIVED]: RequestWorkflowArchivedMeta;

  [REQUEST_WORKFLOW_EVENT_TYPE.REQUEST_WORKFLOW_REQUEST_PRODUCT_ID_UPDATED]: {
    oldRequestProductAssocId?: number | null;
    newRequestProductAssocId: number;
  };

  [REQUEST_WORKFLOW_EVENT_TYPE.REQUEST_MISTAKES_UPDATED]: Array<{
    mistakeTypeId: RequestMistakeType['id'];
    mistakeTypeName: RequestMistakeType['name'];
    comment?: RequestMistakeRequestAssoc['comment'];
  }>;

  [REQUEST_WORKFLOW_EVENT_TYPE.FILE_UPLOADED_TO_SUBMISSION]: {
    submissionId: WorkflowFormSubmission['id'];
    formId: WorkflowNodeForm['id'];
    formTitle: WorkflowNodeForm['title'];
    formFieldKey: WorkflowFormField['key'];
    formFieldTitle: WorkflowFormField['title'];
    requestFileId: RequestFile['id'];
    submissionFileId: WorkflowFormSubmissionFile['id'];
    fileCatId: RequestFileCategory['id'];
    fileCatName: RequestFileCategory['name'];
    fileName: RequestFile['filename'];
  };

  [REQUEST_WORKFLOW_EVENT_TYPE.FILE_DELETED_FROM_SUBMISSION]: RequestWorkflowEventMetaByType[typeof REQUEST_WORKFLOW_EVENT_TYPE.FILE_UPLOADED_TO_SUBMISSION];

  [REQUEST_WORKFLOW_EVENT_TYPE.REQUEST_WORKFLOW_SUBMISSION_SYNCED]: {
    submissionId: WorkflowFormSubmission['id'];
    isNewSubmission: boolean;
    formId: WorkflowNodeForm['id'];
    formTitle: WorkflowNodeForm['title'];
    syncedSubmissionData: WorkflowFormSubmission['data']; // новые (полученные) данные сабмишена
    isSubmissionValid: boolean;
  };
};

// Получаем meta для типа события: если не указан в WorkflowEventMetaByType, то undefined
type MetaForEventType<T extends RequestWorkflowEventType> =
  T extends keyof RequestWorkflowEventMetaByType
    ? RequestWorkflowEventMetaByType[T]
    : undefined;

// Тип события с правильным типом meta в зависимости от eventType
export type RequestWorkflowEventOfType<T extends RequestWorkflowEventType> = {
  eventType: T;
} & (MetaForEventType<T> extends undefined
  ? { meta?: undefined }
  : { meta: MetaForEventType<T> });

// Автоматически создаем union для всех типов событий
export type RequestWorkflowEvent = {
  [K in RequestWorkflowEventType]: BaseRequestWorkflowEvent &
    RequestWorkflowEventActor &
    RequestWorkflowEventOfType<K>;
}[RequestWorkflowEventType];

// Описание типа Event в простом виде:
// export type RequestWorkflowEvent = {
//   id: number;
//   requestWorkflowId: RequestWorkflow['id'];
//   eventType: RequestWorkflowEventType;
//   actorType: RequestWorkflowActorType;
//   actorId?: User['id']; // userId, если actorType = 'user'
//   meta?: Record<string, any>; // зависит от eventType
//   createdAt: Date;
// };

export type RequestWorkflowEventUser = Pick<
  User,
  'id' | 'fullname' | 'email' | 'avatar'
> & {
  person: Pick<
    Person,
    | 'id'
    | 'firstName'
    | 'lastName'
    | 'patronymic'
    | 'fio'
    | 'fioWithoutPatronymic'
  >;
};

export type RequestWorkflowEventExternal = RequestWorkflowEvent &
  (
    | {
        actorType: typeof REQUEST_WORKFLOW_ACTOR_TYPE.USER;
        user: RequestWorkflowEventUser;
      }
    | { actorType: typeof REQUEST_WORKFLOW_ACTOR_TYPE.SYSTEM; user?: undefined }
  );
